// RITA-1525 — Vertragsdokument → HTML.
//
// Die Oberfläche speichert nur `document`; der Server leitet `content` als reinen Text daraus ab.
// Der HTML-Export bleibt für den getrennten HTML-Import und dessen Prüfungen erhalten. Er nutzt
// p, br, b, i, u, ul, li sowie <hr data-seitenumbruch> und <span data-kaestchen>. Platzhalter stehen
// als {{KEY}}-Text, mit ihrem Format in denselben Tags wie Text (RITA-1533); ein wörtliches {{KEY}} im
// Text würde beim Re-Import zum Platzhalter.
import { KAESTCHEN_ANGEKREUZT, KAESTCHEN_LEER } from './erzeugen';
import { Block, Inlineknoten, Vertragsdokument, Zeichenformat } from './types';

export function htmlEscape(wert: string): string {
  return wert.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function mitTags(html: string, format: Zeichenformat): string {
  let ergebnis = html;
  if (format.unterstrichen) ergebnis = `<u>${ergebnis}</u>`;
  if (format.kursiv) ergebnis = `<i>${ergebnis}</i>`;
  if (format.fett) ergebnis = `<b>${ergebnis}</b>`;
  // Eigene Schriftgröße (RITA-1534) als äußere Stilangabe; der Import liest sie zurück.
  if (format.groessePt !== undefined) ergebnis = `<span style="font-size:${format.groessePt}pt">${ergebnis}</span>`;
  return ergebnis;
}

function inlineZuHtml(knoten: Inlineknoten): string {
  switch (knoten.typ) {
    case 'text':
      return mitTags(htmlEscape(knoten.text), knoten);
    case 'platzhalter':
      return mitTags(`{{${knoten.schluessel}}}`, knoten);
    case 'kaestchen':
      return `<span data-kaestchen="${knoten.angekreuzt ? 'angekreuzt' : 'leer'}">${
        knoten.angekreuzt ? KAESTCHEN_ANGEKREUZT : KAESTCHEN_LEER
      }</span>`;
    case 'zeilenumbruch':
      return '<br>';
    default:
      return '';
  }
}

export function inlinesZuHtml(inhalt: Inlineknoten[]): string {
  return inhalt.map(inlineZuHtml).join('');
}

export function blockZuHtml(block: Block): string {
  switch (block.typ) {
    case 'absatz':
      return `<p>${inlinesZuHtml(block.inhalt)}</p>`;
    case 'liste':
      return `<ul>${block.eintraege.map((eintrag) => `<li>${inlinesZuHtml(eintrag)}</li>`).join('')}</ul>`;
    case 'seitenumbruch':
      return '<hr data-seitenumbruch="true">';
    default:
      return '';
  }
}

export function bloeckeZuHtml(bloecke: Block[]): string {
  return bloecke.map(blockZuHtml).join('');
}

/** HTML des Vertragstexts (ohne Kopf- und Fußzeile); nicht die Speicherform der Vorlage. */
export function dokumentZuHtml(dokument: Vertragsdokument): string {
  return bloeckeZuHtml(dokument.text);
}

/** Sichtbarer Text ohne Auszeichnung, z. B. für Prüfungen auf leeren Inhalt. */
export function dokumentZuText(dokument: Vertragsdokument): string {
  const inlineText = (inhalt: Inlineknoten[]): string =>
    inhalt
      .map((knoten) => {
        if (knoten.typ === 'text') return knoten.text;
        if (knoten.typ === 'platzhalter') return `{{${knoten.schluessel}}}`;
        if (knoten.typ === 'kaestchen') return knoten.angekreuzt ? KAESTCHEN_ANGEKREUZT : KAESTCHEN_LEER;
        return '\n';
      })
      .join('');
  return dokument.text
    .map((block) => {
      if (block.typ === 'absatz') return inlineText(block.inhalt);
      if (block.typ === 'liste') return block.eintraege.map(inlineText).join('\n');
      return '';
    })
    .join('\n');
}
